import { useEffect, useRef, useState } from "react";
import { downloadRoomMedia } from "../api";
import { loadMedia } from "../storage/quiz-store";

function useObjectUrl(loader: (() => Promise<Blob | undefined>) | undefined, key: string | undefined): string | undefined {
  const [url, setUrl] = useState<string>();
  useEffect(() => {
    let active = true;
    let created: string | undefined;
    setUrl(undefined);
    if (!loader || !key) return;
    void loader()
      .then((blob) => {
        if (!active || !blob) return;
        created = URL.createObjectURL(blob);
        setUrl(created);
      })
      .catch(() => setUrl(undefined));
    return () => {
      active = false;
      if (created) URL.revokeObjectURL(created);
    };
  }, [key]);
  return url;
}

export function LocalMediaPreview({ mediaId, kind }: { mediaId?: string; kind: "image" | "audio" }) {
  const loader = mediaId ? () => loadMedia(mediaId) : undefined;
  const url = useObjectUrl(loader, mediaId);
  if (!url) return null;
  return kind === "image" ? (
    <img className="media-preview" src={url} alt="Medienvorschau" />
  ) : (
    <audio className="audio-preview" src={url} controls preload="metadata" />
  );
}

export function RoomImage({
  roomId,
  token,
  mediaId,
  className,
}: {
  roomId?: string;
  token?: string;
  mediaId?: string;
  className?: string;
}) {
  const loader = roomId && token && mediaId ? () => downloadRoomMedia(roomId, token, mediaId) : undefined;
  const url = useObjectUrl(loader, roomId && token && mediaId ? `${roomId}:${token}:${mediaId}` : undefined);
  return url ? <img className={className ?? "question-media"} src={url} alt="" /> : null;
}

export function SharedAudio({
  roomId,
  token,
  mediaId,
  playing,
}: {
  roomId?: string;
  token?: string;
  mediaId?: string;
  playing: boolean;
}) {
  const loader = roomId && token && mediaId ? () => downloadRoomMedia(roomId, token, mediaId) : undefined;
  const url = useObjectUrl(loader, roomId && token && mediaId ? `${roomId}:${token}:${mediaId}` : undefined);
  const audioRef = useRef<HTMLAudioElement>(null);
  const [blocked, setBlocked] = useState(false);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !url) return;
    if (!playing) {
      audio.pause();
      audio.currentTime = 0;
      setBlocked(false);
      return;
    }
    audio.currentTime = 0;
    void audio.play().then(() => setBlocked(false)).catch(() => setBlocked(true));
  }, [playing, url]);

  if (!url) return null;
  return (
    <div className="shared-audio">
      <audio ref={audioRef} src={url} preload="auto" />
      {blocked ? (
        <button className="button button--accent button--small" onClick={() => void audioRef.current?.play()}>
          Show-Audio aktivieren
        </button>
      ) : null}
    </div>
  );
}
